import React, { useState, useEffect } from 'react';
import { saveMetrics, loadLatestMetrics, loadAllBloodMarkers } from '../db';
import { BIOMARKER_GROUPS } from '../biomarkers';
import PDFUpload from './PDFUpload';
import { Field } from './FormFields';

const s = {
  card: { background: 'var(--bg-card)', border: '1px solid var(--border2)', borderRadius: 'var(--radius-lg)', padding: '1.5rem', marginBottom: '1rem' },
  label: { fontSize: '.65rem', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.75rem' },
  saveBtn: (saving) => ({ width: '100%', padding: '.75rem', borderRadius: 'var(--radius)', border: 'none', background: saving ? 'var(--bg-elevated)' : 'var(--primary)', color: saving ? 'var(--text3)' : '#080f18', fontFamily: 'var(--font-body)', fontSize: '.85rem', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 500, marginTop: '.75rem', transition: 'background .2s' }),
};

export default function UploadData() {
  const [activeTab, setActiveTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');
  const [loading, setLoading] = useState(true);

  const [bloodData, setBloodData] = useState({});
  const [body, setBody] = useState({ height: '', weight: '', waist: '', hip: '' });
  const [mental, setMental] = useState({ stressScore: '', phq9: '', gad7: '', burnoutScore: '', sleepHours: '' });
  const [fitness, setFitness] = useState({ stepsPerDay: '', workoutsPerWeek: '', restingHR: '', activeMinutes: '' });

  useEffect(() => {
    async function loadAll() {
      setLoading(true);
      try {
        const [all, bo, m, f] = await Promise.all([
          loadAllBloodMarkers(), loadLatestMetrics('body'),
          loadLatestMetrics('mental'), loadLatestMetrics('fitness')
        ]);
        if (all) setBloodData(prev => ({ ...prev, ...all }));
        if (bo) setBody(prev => ({ ...prev, ...bo }));
        if (m) setMental(prev => ({ ...prev, ...m }));
        if (f) setFitness(prev => ({ ...prev, ...f }));
      } catch (e) { console.error(e); }
      setLoading(false);
    }
    loadAll();
  }, []);

  async function handleSaveBlood() {
    setSaving(true); setSaved('');
    try {
      const coreKeys = ['fastingGlucose','hba1c','totalCholesterol','hdl','ldl','triglycerides','nonHdl','cholHdlRatio'];
      const core = {}, extended = {};
      Object.entries(bloodData).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) {
          if (coreKeys.includes(k)) core[k] = v; else extended[k] = v;
        }
      });
      if (Object.keys(core).length > 0) await saveMetrics('blood', core);
      if (Object.keys(extended).length > 0) await saveMetrics('extendedBlood', extended);
      setSaved('blood'); setTimeout(() => setSaved(''), 4000);
    } catch (e) { alert('Error saving. Please try again.'); }
    setSaving(false);
  }

  async function handleSave(category, data) {
    setSaving(true); setSaved('');
    try {
      const clean = {};
      Object.entries(data).forEach(([k, v]) => { if (v !== '' && v !== null) clean[k] = v; });
      await saveMetrics(category, clean);
      setSaved(category); setTimeout(() => setSaved(''), 4000);
    } catch (e) { alert('Error saving. Please try again.'); }
    setSaving(false);
  }

  const tabs = [
    { label: 'Blood Results', icon: '🔬' },
    { label: 'Body Metrics', icon: '⚖️' },
    { label: 'Mental Health', icon: '🧠' },
    { label: 'Fitness', icon: '🏃' }
  ];

  if (loading) return (
    <div style={{ padding: '2rem 0' }}>
      <div style={{ width: '180px', height: '28px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', marginBottom: '.5rem' }} />
      <div style={{ width: '280px', height: '14px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius)' }} />
    </div>
  );

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: '1.75rem' }}>
        <h1 style={{ fontFamily: 'var(--font-head)', fontSize: '1.6rem', fontWeight: 600, color: 'var(--text)', letterSpacing: '-.02em', marginBottom: '.2rem' }}>Upload Data</h1>
        <p style={{ fontSize: '.77rem', color: 'var(--text3)', fontWeight: 300 }}>Your data is used by Rico to give you personalised health advice — the more you record, the better the insights.</p>
      </div>

      {/* Saved confirmation */}
      {saved && (
        <div style={{ padding: '.75rem 1.1rem', borderRadius: 'var(--radius)', fontSize: '.78rem', marginBottom: '1rem', background: 'var(--success-dim)', border: '1px solid rgba(0,200,150,.2)', color: 'var(--success)', display: 'flex', alignItems: 'center', gap: '.6rem' }}>
          <span>✓</span> Saved — Rico will use your updated data in your next conversation.
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '.3rem', marginBottom: '1.5rem', borderBottom: '1px solid var(--border2)' }}>
        {tabs.map((t, i) => (
          <button key={i} onClick={() => setActiveTab(i)} style={{
            padding: '.6rem 1rem', background: 'transparent', border: 'none',
            color: activeTab === i ? 'var(--primary)' : 'var(--text3)',
            fontFamily: 'var(--font-body)', fontSize: '.8rem', cursor: 'pointer',
            borderBottom: activeTab === i ? '2px solid var(--primary)' : '2px solid transparent',
            marginBottom: '-1px', fontWeight: activeTab === i ? 500 : 400,
            display: 'flex', alignItems: 'center', gap: '.4rem', transition: 'color .15s'
          }}>
            <span style={{ fontSize: '.9rem' }}>{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── BLOOD RESULTS ── */}
      {activeTab === 0 && (
        <div>
          {/* PDF Upload */}
          <div style={s.card}>
            <div style={s.label}>Upload Blood Test Document</div>
            <p style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300, marginBottom: '1rem', lineHeight: 1.6 }}>
              Upload a PDF or photo of your blood test results and Rico will extract all values automatically. Works with NHS, private labs and international formats.
            </p>
            <PDFUpload onSaved={(vals) => setBloodData(prev => ({ ...prev, ...vals }))} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', margin: '1.5rem 0 1rem' }}>
            <div style={{ flex: 1, height: '0.5px', background: 'var(--border2)' }} />
            <span style={{ fontSize: '.72rem', color: 'var(--text3)', fontWeight: 300 }}>or enter manually</span>
            <div style={{ flex: 1, height: '0.5px', background: 'var(--border2)' }} />
          </div>

          {Object.entries(BIOMARKER_GROUPS).map(([groupKey, group]) => (
            <div key={groupKey} style={s.card}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', marginBottom: '1rem' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: group.color, boxShadow: `0 0 6px ${group.color}55` }} />
                <div style={{ fontSize: '.85rem', fontWeight: 500, color: 'var(--text)' }}>{group.label}</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem' }}>
                {Object.entries(group.markers).map(([key, marker]) => (
                  <Field key={key} label={marker.label + ' (' + marker.unit + ')'} value={bloodData[key]} onChange={v => setBloodData(prev => ({ ...prev, [key]: v }))} stepVal="0.01" />
                ))}
              </div>
            </div>
          ))}

          <button onClick={handleSaveBlood} disabled={saving} style={s.saveBtn(saving)}>
            {saving ? 'Saving…' : '✓ Save All Blood Results'}
          </button>
        </div>
      )}

      {/* ── BODY METRICS ── */}
      {activeTab === 1 && (
        <div>
          <div style={s.card}>
            <div style={s.label}>Body Measurements</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem', marginBottom: '.5rem' }}>
              <Field label="Height (cm)" value={body.height} onChange={v => setBody(p => ({ ...p, height: v }))} step="1" />
              <Field label="Weight (kg)" value={body.weight} onChange={v => setBody(p => ({ ...p, weight: v }))} step="0.1" />
              <Field label="Waist Circumference (cm)" value={body.waist} onChange={v => setBody(p => ({ ...p, waist: v }))} step="0.5" />
              <Field label="Hip Circumference (cm)" value={body.hip} onChange={v => setBody(p => ({ ...p, hip: v }))} step="0.5" />
            </div>
            {body.height && body.weight && (
              <div style={{ padding: '.75rem 1rem', background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', fontSize: '.78rem', color: 'var(--text3)', marginTop: '.5rem' }}>
                BMI: <span style={{ color: 'var(--text)', fontWeight: 500 }}>{(parseFloat(body.weight) / Math.pow(parseFloat(body.height) / 100, 2)).toFixed(1)} kg/m²</span>
              </div>
            )}
            <button onClick={() => handleSave('body', body)} disabled={saving} style={s.saveBtn(saving)}>
              {saving ? 'Saving…' : '✓ Save Body Metrics'}
            </button>
          </div>

          <div style={{ ...s.card, background: 'rgba(0,174,239,0.04)', border: '1px solid rgba(0,174,239,0.15)' }}>
            <div style={{ fontSize: '.72rem', fontWeight: 600, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.5rem' }}>Why this matters</div>
            <p style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.6 }}>
              BMI and waist circumference are strong predictors of metabolic health. A waist above 94cm (men) or 80cm (women) significantly increases the risk of type 2 diabetes and cardiovascular disease — even with a healthy BMI.
            </p>
          </div>
        </div>
      )}

      {/* ── MENTAL HEALTH ── */}
      {activeTab === 2 && (
        <div>
          <div style={s.card}>
            <div style={s.label}>Mental Wellbeing Scores</div>
            <p style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300, marginBottom: '1rem', lineHeight: 1.6 }}>
              These scores help Rico understand the full picture of your health. Chronic stress and poor sleep directly impact your blood markers.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem' }}>
              <Field label="Stress Score (/100)" value={mental.stressScore} onChange={v => setMental(p => ({ ...p, stressScore: v }))} step="1" />
              <Field label="Burnout Score (/100)" value={mental.burnoutScore} onChange={v => setMental(p => ({ ...p, burnoutScore: v }))} step="1" />
              <Field label="PHQ-9 Depression Score (/27)" value={mental.phq9} onChange={v => setMental(p => ({ ...p, phq9: v }))} step="1" />
              <Field label="GAD-7 Anxiety Score (/21)" value={mental.gad7} onChange={v => setMental(p => ({ ...p, gad7: v }))} step="1" />
              <Field label="Average Sleep (hrs/night)" value={mental.sleepHours} onChange={v => setMental(p => ({ ...p, sleepHours: v }))} step="0.5" />
            </div>
            <button onClick={() => handleSave('mental', mental)} disabled={saving} style={s.saveBtn(saving)}>
              {saving ? 'Saving…' : '✓ Save Mental Health Scores'}
            </button>
          </div>

          <div style={{ ...s.card, background: 'rgba(0,174,239,0.04)', border: '1px solid rgba(0,174,239,0.15)' }}>
            <div style={{ fontSize: '.72rem', fontWeight: 600, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.5rem' }}>Stress & your blood results</div>
            <p style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.6 }}>
              Chronic stress elevates cortisol, which raises fasting glucose, increases LDL cholesterol and suppresses immune function. Addressing stress is one of the highest-impact interventions for improving blood markers.
            </p>
          </div>
        </div>
      )}

      {/* ── FITNESS ── */}
      {activeTab === 3 && (
        <div>
          <div style={s.card}>
            <div style={s.label}>Fitness & Activity</div>
            <p style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300, marginBottom: '1rem', lineHeight: 1.6 }}>
              Activity data helps Rico give you more accurate advice. You can also sync this automatically from your wearable in the Wearables tab.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.6rem' }}>
              <Field label="Average Steps / Day" value={fitness.stepsPerDay} onChange={v => setFitness(p => ({ ...p, stepsPerDay: v }))} step="100" />
              <Field label="Workouts / Week" value={fitness.workoutsPerWeek} onChange={v => setFitness(p => ({ ...p, workoutsPerWeek: v }))} step="0.5" />
              <Field label="Resting Heart Rate (bpm)" value={fitness.restingHR} onChange={v => setFitness(p => ({ ...p, restingHR: v }))} step="1" />
              <Field label="Active Minutes / Day" value={fitness.activeMinutes} onChange={v => setFitness(p => ({ ...p, activeMinutes: v }))} step="1" />
            </div>
            <button onClick={() => handleSave('fitness', fitness)} disabled={saving} style={s.saveBtn(saving)}>
              {saving ? 'Saving…' : '✓ Save Fitness Data'}
            </button>
          </div>

          <div style={{ ...s.card, background: 'rgba(0,200,150,0.04)', border: '1px solid rgba(0,200,150,0.15)' }}>
            <div style={{ fontSize: '.72rem', fontWeight: 600, color: 'var(--success)', textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: '.5rem' }}>Target activity levels</div>
            <p style={{ fontSize: '.78rem', color: 'var(--text3)', fontWeight: 300, lineHeight: 1.6 }}>
              WHO recommends 8,000–10,000 steps/day and 150 minutes of moderate activity per week. Even a 10% increase in daily steps measurably improves glucose control, blood pressure and cholesterol.
            </p>
          </div>
        </div>
      )}

      <div style={{ marginTop: '1.5rem', fontSize: '.7rem', color: 'var(--text3)', lineHeight: 1.6, textAlign: 'center', fontWeight: 300 }}>
        🔒 Your data is stored securely and privately. Only you and Levare's clinical team can access your individual results.
      </div>
    </div>
  );
}
